import { useState, useCallback, useEffect } from 'react';
import { Paper, Tooltip } from '@mui/material';
import { Spinner, Input } from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import {
  getInspectionFrequencies,
  createInspectionFrequency,
  updateInspectionFrequency,
  deleteInspectionFrequency,
} from './FormikHelpers/Api';
import './inspection-tracker.scss';

const emptyFreq = { name: '', days: '', description: '' };

const FInput = ({ value, onChange, placeholder, readOnly, type = 'text', min, max }) => (
  <Input
    type={type}
    bsSize="sm"
    value={value ?? ''}
    onChange={(e) => !readOnly && onChange(e.target.value)}
    placeholder={placeholder}
    readOnly={readOnly}
    min={min}
    max={max}
    style={{ borderColor: '#D4E8DC', borderRadius: 7, fontSize: '0.82rem', backgroundColor: readOnly ? '#f8f9fa' : undefined }}
  />
);

const frequencyLabel = (days) => {
  if (!days) return '—';
  if (days === 1) return 'Daily';
  if (days === 7) return 'Weekly';
  if (days === 14) return 'Bi-Weekly';
  if (days === 30) return 'Monthly';
  if (days === 90) return 'Quarterly';
  if (days === 180) return 'Semi-Annual';
  if (days === 365) return 'Yearly';
  if (days === 730) return 'Every 2 Years';
  if (days < 7) return `Every ${days} days`;
  if (days < 30) return `Every ${Math.round(days / 7)}w`;
  if (days < 365) return `Every ${Math.round(days / 30)}mo`;
  return `Every ${(days / 365).toFixed(1)}yr`;
};

const InlineFreqRow = ({ data, isNew, onSave, onCancel }) => {
  const [row, setRow] = useState({ ...data });
  const [saving, setSaving] = useState(false);

  const set = (k) => (v) => setRow((p) => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!row.name?.trim()) { toast.error('Name is required'); return; }
    const days = Number(row.days);
    if (!days || !Number.isInteger(days) || days < 1) { toast.error('Days must be a positive whole number'); return; }
    setSaving(true);
    try {
      await onSave({ ...row, days });
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr className={`it-editable-row it-editable-row--${isNew ? 'new' : 'edit'}`}>
      <td className={`it-editable-td it-editable-td--${isNew ? 'new' : 'edit'}`} style={{ width: 40, textAlign: 'center' }}>
        <div className={`it-expand-icon it-expand-icon--${isNew ? 'new' : 'edit'}`}>
          <FeatherIcon icon={isNew ? 'plus' : 'edit-2'} size={12} color={isNew ? '#1A6B4A' : '#1D4ED8'} />
        </div>
      </td>
      <td className={`it-editable-td it-editable-td--${isNew ? 'new' : 'edit'}`} style={{ minWidth: 180 }}>
        <FInput value={row.name} onChange={set('name')} placeholder="Frequency name..." />
      </td>
      <td className={`it-editable-td it-editable-td--${isNew ? 'new' : 'edit'}`} style={{ minWidth: 120 }}>
        <FInput type="number" value={row.days} onChange={set('days')} placeholder="e.g. 365" min="1" />
      </td>
      <td className={`it-editable-td it-editable-td--${isNew ? 'new' : 'edit'}`} style={{ minWidth: 120, textAlign: 'center' }}>
        <span style={{ fontSize: '0.78rem', color: '#1A6B4A', fontWeight: 600 }}>
          {row.days ? frequencyLabel(Number(row.days)) : '—'}
        </span>
      </td>
      <td className={`it-editable-td it-editable-td--${isNew ? 'new' : 'edit'}`} style={{ minWidth: 220 }}>
        <FInput value={row.description} onChange={set('description')} placeholder="Description (optional)..." />
      </td>
      <td className={`it-editable-td it-editable-td--${isNew ? 'new' : 'edit'}`} style={{ width: 100, textAlign: 'center' }}>
        <span style={{ fontSize: '0.75rem', color: '#6B8F7C' }}>—</span>
      </td>
      <td className={`it-editable-td it-editable-td--${isNew ? 'new' : 'edit'}`} style={{ width: 130, textAlign: 'center' }}>
        <div className="it-action-group">
          <button type="button" className="it-btn it-btn--cancel" onClick={onCancel} disabled={saving}>
            <FeatherIcon icon="x" size={12} /> Cancel
          </button>
          <button type="button" className={`it-btn it-btn--save${saving ? ' it-btn--saving' : ''}`} onClick={handleSave} disabled={saving}>
            {saving ? <Spinner size="sm" /> : <FeatherIcon icon="check" size={12} />}
            {isNew ? 'Add' : 'Save'}
          </button>
        </div>
      </td>
    </tr>
  );
};

const InspectionFrequenciesTable = ({ onRefresh }) => {
  const [frequencies, setFrequencies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNewRow, setShowNewRow] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  const fetchFrequencies = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getInspectionFrequencies(showInactive ? {} : { activeOnly: 'true' });
      setFrequencies(res?.data || []);
    } catch {
      toast.error('Failed to load inspection frequencies');
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => { fetchFrequencies(); }, [fetchFrequencies]);

  const handleCreate = async (row) => {
    await createInspectionFrequency({ name: row.name.trim(), days: row.days, description: row.description?.trim() || '' });
    toast.success('Frequency created');
    setShowNewRow(false);
    fetchFrequencies();
    onRefresh?.();
  };

  const handleUpdate = async (id, row) => {
    await updateInspectionFrequency(id, { name: row.name.trim(), days: row.days, description: row.description?.trim() || '' });
    toast.success('Frequency updated');
    setEditingId(null);
    fetchFrequencies();
    onRefresh?.();
  };

  const handleDelete = async (freq) => {
    const res = await Swal.fire({
      title: 'Deactivate Frequency?',
      html: `<p>Deactivate <strong>${freq.name}</strong> (${freq.days} days)?</p>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#DC2626',
      confirmButtonText: 'Deactivate',
      cancelButtonText: 'Cancel',
      width: 400,
    });
    if (!res.isConfirmed) return;
    try {
      await deleteInspectionFrequency(freq._id);
      toast.success('Frequency deactivated');
      fetchFrequencies();
      onRefresh?.();
    } catch {
      toast.error('Failed to deactivate frequency');
    }
  };

  return (
    <Paper elevation={0} className="it-wrap it-paper">
      <div className="it-header">
        <div className="it-header__left">
          <div className="it-header__icon-wrap">
            <FeatherIcon icon="clock" size={18} color="#A7F3D0" />
          </div>
          <div className="it-header__meta">
            <div className="it-header__label">Configuration</div>
            <div className="it-header__title">Inspection Frequencies</div>
          </div>
          {frequencies.length > 0 && (
            <div className="it-header__badges">
              <span className="it-header__badge it-header__badge--records">{frequencies.length} frequenc{frequencies.length !== 1 ? 'ies' : 'y'}</span>
            </div>
          )}
        </div>
        <div className="it-header__actions" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'rgba(167,243,208,0.75)', fontSize: '0.78rem', cursor: 'pointer', marginBottom: 0 }}>
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} style={{ cursor: 'pointer' }} />
            Show inactive
          </label>
          <button type="button" className={`it-btn it-add ${showNewRow ? 'it-btn--danger' : 'it-btn--primary-solid'}`} onClick={() => { setShowNewRow((v) => !v); setEditingId(null); }}>
            <FeatherIcon icon={showNewRow ? 'x' : 'plus'} size={14} />
            {showNewRow ? 'Cancel' : 'Add Frequency'}
          </button>
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className="it-tbl" style={{ minWidth: "100%" }}>
          <thead>
            <tr>
              <th style={{ width: 40 }}></th>
              <th style={{ minWidth: 180 }}>Name</th>
              <th style={{ minWidth: 120 }}>Days</th>
              <th style={{ minWidth: 120 }}>Equivalent</th>
              <th style={{ minWidth: 220 }}>Description</th>
              <th style={{ width: 100 }}>Status</th>
              <th style={{ width: 130 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {showNewRow && (
              <InlineFreqRow
                data={{ ...emptyFreq }}
                isNew
                onSave={handleCreate}
                onCancel={() => setShowNewRow(false)}
              />
            )}

            {loading && (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: '32px 0' }}>
                  <Spinner color="success" />
                </td>
              </tr>
            )}

            {!loading && frequencies.length === 0 && !showNewRow && (
              <tr>
                <td colSpan={7} className="it-empty-cell">
                  <div className="it-empty" style={{ minHeight: '30vh' }}>
                    <div className="it-empty__icon-wrap"><FeatherIcon icon="clock" size={24} color="#1A6B4A" /></div>
                    <div className="it-empty__title">No Inspection Frequencies</div>
                    <div className="it-empty__sub">Click "Add Frequency" to create your first frequency.</div>
                  </div>
                </td>
              </tr>
            )}

            {!loading && frequencies.map((freq) => {
              if (editingId === freq._id) {
                return (
                  <InlineFreqRow
                    key={freq._id}
                    data={freq}
                    isNew={false}
                    onSave={(row) => handleUpdate(freq._id, row)}
                    onCancel={() => setEditingId(null)}
                  />
                );
              }
              return (
                <tr key={freq._id} className="it-row" onClick={() => setEditingId(freq._id)}>
                  <td className="it-expand-td">
                    <div className="it-expand-icon it-expand-icon--view"><FeatherIcon icon="edit-3" size={12} color="#1A6B4A" /></div>
                  </td>
                  <td>
                    <span style={{ fontWeight: 700, color: '#1A2D24', fontSize: '0.82rem' }}>{freq.name}</span>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <span className="it-cell-year">{freq.days}</span>
                    <span style={{ fontSize: '0.7rem', color: '#6B8F7C', marginLeft: 3 }}>days</span>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <span className="it-badge it-badge--type">{frequencyLabel(freq.days)}</span>
                  </td>
                  <td>
                    <span style={{ fontSize: '0.80rem', color: '#6B8F7C' }}>{freq.description || <span className="it-cell-muted">—</span>}</span>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <span className={`it-badge ${freq.isActive ? 'it-badge--ok' : 'it-badge--overdue'}`}>
                      {freq.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="it-actions-td" onClick={(e) => e.stopPropagation()}>
                    <div className="it-action-group">
                      <Tooltip title="Edit" arrow>
                        <button type="button" className="it-ebtn" onClick={(e) => { e.stopPropagation(); setEditingId(freq._id); }}>
                          <FeatherIcon icon="edit-2" size={13} color="#1A6B4A" />
                        </button>
                      </Tooltip>
                      <Tooltip title="Deactivate" arrow>
                        <button type="button" className="it-dbtn" onClick={(e) => { e.stopPropagation(); handleDelete(freq); }}>
                          <FeatherIcon icon="trash-2" size={13} color="#DC2626" />
                        </button>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Paper>
  );
};

export { InspectionFrequenciesTable };